import { Inject, Injectable } from '@angular/core';
import {mbeConfigService} from './mbe-config.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import {ChartOfAccount, ChartOfAccountAPI} from '../..';
import { BasicResponse, SortFilterInterface } from '@auth-suite-services/services';
import { normalizeChartOfAccountForApi } from '../util/coa-request-payload.util';


@Injectable({
  providedIn: 'root',
})
export class ChartOfAccountService {
  private URL; 
  headers: HttpHeaders;

  constructor(
    @Inject(mbeConfigService) private config,
    private httpClient: HttpClient
  ) {
    this.URL =
      config.REST_SERVER_HOST +
      config.REST_SERVER_url.find((restUrl) => restUrl.api === 'MBE').url +
      '/coa';
    this.headers = new HttpHeaders().set('Content-Type', 'application/json');
  }

  getAllChartOfAccount(
    pageIndex?: number,
    pageSize?: number,
    sortActive?: string,
    sort?: string,
    sortFilter?: SortFilterInterface
  ): Observable<ChartOfAccountAPI> {
    const headers = this.headers;
    // PK 09/02/2026 - Send empty object when payload is empty/0 so backend gets {} not number (avoids parsing error)
    // const body = sortFilter;
    const body =
      sortFilter && typeof sortFilter === 'object' && !Array.isArray(sortFilter)
        ? sortFilter
        : {};
    // tslint:disable-next-line:max-line-length
    return this.httpClient.post<ChartOfAccountAPI>(
      `${this.URL}/allchartofaccount/${pageIndex}/${pageSize}/${
        sortActive ? sortActive : 'id'
      }/${sort ? sort : 'asc'}`,
      body,
      { headers }
    );
  }

  getChartOfAccount(id: number): Observable<ChartOfAccount> {
    const headers = this.headers;
    return this.httpClient.get<ChartOfAccount>(
      `${this.URL}/getchartofaccount/${id}`,
      { headers }
    );
  }

  /** Suggested NominalAccountNo (next Gid-Rno) for wizard/COA autofill. 10/03/2026 PK. */
  getSuggestedNominalAccountNo(): Observable<{ nominalAccountNo: string }> {
    const headers = this.headers;
    return this.httpClient.get<{ nominalAccountNo: string }>(
      `${this.URL}/getsuggestednominalaccountno`,
      { headers }
    );
  }

  updateChartOfAccount(coa: ChartOfAccount): Observable<BasicResponse> {
    const headers = this.headers;
    // Normalize checkbox/integer fields for ChartOfAccountRequest. 15/09/2026 PK.
    const body = normalizeChartOfAccountForApi(
      coa as unknown as Record<string, unknown>
    );
    return this.httpClient.post<BasicResponse>(
      `${this.URL}/updatechartofaccount`,
      body,
      { headers }
    );
  }

  // Added createChartOfAccount for COA create and FIN wizard. 04/06/2026 PK.
  createChartOfAccount(coa: ChartOfAccount): Observable<BasicResponse> {
    const headers = this.headers;
    // Normalize checkbox/integer fields for ChartOfAccountRequest. 15/09/2026 PK.
    const body = normalizeChartOfAccountForApi(
      coa as unknown as Record<string, unknown>
    );
    return this.httpClient.post<BasicResponse>(
      `${this.URL}/createchartofaccount`,
      body,
      { headers }
    );
  }

  // Removed duplicate getSuggestedNominalAccountNo; method already defined above. 04/06/2026 PK.
  // getSuggestedNominalAccountNo(): Observable<{ nominalAccountNo: string }> {
  //   const headers = this.headers;
  //   return this.httpClient.get<{ nominalAccountNo: string }>(
  //     `${this.URL}/getsuggestednominalaccountno`,
  //     { headers }
  //   );
  // }
  // See C001 - 2026-06-08 - HE
  // // Restored getSuggestedNominalAccountNo for FIN wizard COA autofill (was removed in error). 06/06/2026 PK.
  // getSuggestedNominalAccountNo(): Observable<{ nominalAccountNo: string }> {
  //   const headers = this.headers;
  //   return this.httpClient.get<{ nominalAccountNo: string }>(
  //     `${this.URL}/getsuggestednominalaccountno`,
  //     { headers }
  //   );
  // }
}

// ========================================
// CHANGE LOG
// ========================================
// C002: Restored single active getSuggestedNominalAccountNo() on ChartOfAccountService after C001 removed both implementations. Previous behavior: fin-wizard-stepper called getSuggestedNominalAccountNo() but no method existed on ChartOfAccountService (TS2339 build failure). New behavior: One getSuggestedNominalAccountNo() GETs /coa/getsuggestednominalaccountno for FIN wizard COA autofill. Reason: portaladmin build failed. Context: Duplicate removal in C001 left no active method. Impact: FIN wizard and portaladmin build compile; no duplicate method. - 2026-06-11 - HE
// C001: Removed duplicate active getSuggestedNominalAccountNo() implementation at lines 96-102 in ChartOfAccountService. Previous behavior: Two identical getSuggestedNominalAccountNo() methods were defined (first at line 60 from 10/03/2026 PK, second restored at line 96 from 06/06/2026 PK), causing TS2393 duplicate function implementation build error across portaladmin and other apps depending on mbe-service. New behavior: Only the original getSuggestedNominalAccountNo() at line 60 remains active; the erroneous second restoration is commented out. Reason: portaladmin production build failed with TS2393 duplicate function implementation. Context: FIN wizard COA autofill still uses the surviving method at line 60 which calls the same /getsuggestednominalaccountno endpoint. Impact: Resolves build failure for portaladmin and all apps sharing mbe-service ChartOfAccountService; no functional change since both methods were identical. - 2026-06-08 - HE